import { useAtomValue } from 'jotai';
import { RefObject, useEffect, useRef } from 'react';

import { v2ZoomAtom } from '../../store/v2/viewer.atom';
import {
  V2_DOUBLE_TAP_ZOOM,
  clampV2Zoom,
} from '../../components/v2/zoom.constants';

/** 두 탭 사이 최대 간격(ms) */
const DOUBLE_TAP_INTERVAL = 300;
/** 탭으로 인정하는 손가락 이동 허용치(px) */
const TAP_MOVE_TOLERANCE = 10;
/** 두 탭 지점 사이 최대 거리(px) */
const DOUBLE_TAP_DISTANCE = 30;
/** 줌 전환 프리뷰 애니메이션 시간(ms) */
const TRANSITION_MS = 180;

/** 탭 대상이 버튼/링크 등 조작 요소면 더블 탭 줌을 발동하지 않는다 */
const isInteractiveTarget = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(
    target.closest('a, button, input, textarea, select, [role="button"]'),
  );

interface Props {
  /** 네이티브 스크롤 컨테이너(터치 이벤트 대상) */
  scrollRef: RefObject<HTMLElement>;
  /** 전환 프리뷰 CSS transform을 적용할 요소 */
  transformTargetRef: RefObject<HTMLElement>;
  /** 전환 종료 시 최종 배율을 커밋하는 앵커 줌 함수 */
  zoomAtPoint: (
    nextZoom: number,
    clientX: number,
    clientY: number,
    pan?: { x: number; y: number },
    options?: { immediate?: boolean },
  ) => void;
}

/**
 * 모바일 더블 탭 줌 토글.
 * 100% 이하에서는 탭 지점을 앵커로 V2_DOUBLE_TAP_ZOOM까지 확대하고,
 * 확대 상태에서는 100%로 복귀한다. 핀치줌과 동일하게 제스처 프리뷰는
 * CSS transform으로 애니메이션한 뒤 종료 시 한 번만 배율을 커밋한다.
 */
const useDoubleTapZoom = ({
  scrollRef,
  transformTargetRef,
  zoomAtPoint,
}: Props) => {
  const zoom = useAtomValue(v2ZoomAtom);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const zoomAtPointRef = useRef(zoomAtPoint);
  zoomAtPointRef.current = zoomAtPoint;

  useEffect(() => {
    const target = scrollRef.current;
    if (!target) {
      return;
    }

    let lastTap: { x: number; y: number; time: number } | null = null;
    let tapStart: { x: number; y: number } | null = null;
    let isAnimating = false;
    let fallbackTimer: number | null = null;
    let cleanupTransition: (() => void) | null = null;

    const triggerZoom = (clientX: number, clientY: number) => {
      const currentZoom = zoomRef.current;
      /* 확대 상태(>100%)면 100% 복귀, 아니면 목표 배율로 확대 */
      const nextZoom = currentZoom > 1.01 ? 1 : clampV2Zoom(V2_DOUBLE_TAP_ZOOM);

      const el = transformTargetRef.current;
      if (!el) {
        zoomAtPointRef.current(nextZoom, clientX, clientY);
        return;
      }

      isAnimating = true;
      const rect = el.getBoundingClientRect();
      el.style.transformOrigin = `${clientX - rect.left}px ${
        clientY - rect.top
      }px`;
      el.style.willChange = 'transform';
      el.style.transition = `transform ${TRANSITION_MS}ms ease-in-out`;
      /* 리플로 강제: transition이 초기 상태부터 적용되게 한다 */
      void el.getBoundingClientRect();
      el.style.transform = `scale(${nextZoom / currentZoom})`;

      const finish = () => {
        if (!isAnimating) {
          return;
        }
        isAnimating = false;
        cleanupTransition = null;
        el.removeEventListener('transitionend', finish);
        if (fallbackTimer !== null) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }

        /*
         * 커밋 전 원복해야 앵커 계산이 변형 전 레이아웃 기준으로 이뤄진다.
         * immediate 커밋으로 같은 태스크에서 배율까지 반영해, 원복된
         * 이전 레이아웃이 한 프레임 노출(플래시)되는 것을 막는다.
         */
        el.style.transition = '';
        el.style.transform = '';
        el.style.transformOrigin = '';
        el.style.willChange = '';
        zoomAtPointRef.current(nextZoom, clientX, clientY, undefined, {
          immediate: true,
        });
      };

      cleanupTransition = finish;
      el.addEventListener('transitionend', finish);
      fallbackTimer = window.setTimeout(finish, TRANSITION_MS + 100);
    };

    const handleTouchStart = (event: globalThis.TouchEvent) => {
      if (event.touches.length !== 1 || isInteractiveTarget(event.target)) {
        /* 멀티 터치(핀치)나 조작 요소 탭은 더블 탭 후보에서 제외한다 */
        tapStart = null;
        lastTap = null;
        return;
      }
      const touch = event.touches[0];
      tapStart = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchMove = (event: globalThis.TouchEvent) => {
      if (!tapStart || event.touches.length !== 1) {
        tapStart = null;
        return;
      }
      const touch = event.touches[0];
      if (
        Math.hypot(touch.clientX - tapStart.x, touch.clientY - tapStart.y) >
        TAP_MOVE_TOLERANCE
      ) {
        tapStart = null;
      }
    };

    const handleTouchEnd = (event: globalThis.TouchEvent) => {
      if (event.touches.length > 0 || !tapStart || isAnimating) {
        tapStart = null;
        return;
      }

      const { x, y } = tapStart;
      tapStart = null;
      const now = Date.now();

      if (
        lastTap &&
        now - lastTap.time <= DOUBLE_TAP_INTERVAL &&
        Math.hypot(x - lastTap.x, y - lastTap.y) <= DOUBLE_TAP_DISTANCE
      ) {
        lastTap = null;
        triggerZoom(x, y);
        return;
      }

      lastTap = { x, y, time: now };
    };

    const handleTouchCancel = () => {
      tapStart = null;
    };

    target.addEventListener('touchstart', handleTouchStart, { passive: true });
    target.addEventListener('touchmove', handleTouchMove, { passive: true });
    target.addEventListener('touchend', handleTouchEnd, { passive: true });
    target.addEventListener('touchcancel', handleTouchCancel, {
      passive: true,
    });

    return () => {
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
      }
      cleanupTransition?.();
      target.removeEventListener('touchstart', handleTouchStart);
      target.removeEventListener('touchmove', handleTouchMove);
      target.removeEventListener('touchend', handleTouchEnd);
      target.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [scrollRef, transformTargetRef]);
};

export default useDoubleTapZoom;
